import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { Toaster } from '@/components/ui/sonner';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { SettingsProvider } from './providers/settings-provider';
import { ThemeProvider } from './providers/theme-provider';
import ErrorBoundary from './components/ErrorBoundary';
import './css/styles.css';

// Configurar QueryClient com opções de cache otimizadas
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutos - dados considerados frescos
      gcTime: 10 * 60 * 1000, // 10 minutos - tempo de garbage collection (antigo cacheTime)
      refetchOnWindowFocus: false, // Não refetch ao focar na janela
      retry: 1, // Tentar apenas 1 vez em caso de erro
      refetchOnMount: true, // Refetch ao montar componente se dados estiverem stale
    },
    mutations: {
      retry: false, // Não tentar novamente em caso de erro em mutações
    },
  },
});

// Configurar persistência de cache no localStorage
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'REACT_QUERY_OFFLINE_CACHE',
});

const isDevelopment = import.meta.env.DEV;

// Garantir que o tema claro seja aplicado por padrão
if (typeof document !== 'undefined') {
  document.documentElement.classList.remove('dark');
  // Limpar qualquer preferência de tema escuro do localStorage
  const currentTheme = localStorage.getItem('vite-theme');
  if (currentTheme === 'dark' || currentTheme === 'system') {
    localStorage.setItem('vite-theme', 'light');
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: 24 * 60 * 60 * 1000, // 24 horas
        }}
      >
        <BrowserRouter>
          <HelmetProvider>
            <SettingsProvider>
              <ThemeProvider>
                <AuthProvider>
                  <Toaster />
                  <App />
                </AuthProvider>
              </ThemeProvider>
            </SettingsProvider>
          </HelmetProvider>
        </BrowserRouter>
        {isDevelopment && <ReactQueryDevtools initialIsOpen={false} />}
      </PersistQueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
